import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowForwardIos, Close, CorporateFareOutlined, PersonOutlined, SupervisorAccountOutlined } from "@mui/icons-material";
import { Chip, FormControl, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";

import "../ecrOps/EcrOpsLayout.css";

function getOrgType(org) {
  const t = (org?.org_type || "").toLowerCase();
  if (t === "us") return "US";
  if (t === "global") return "Global";
  return "Other";
}

const TYPE_COLORS = {
  US: { bg: "#e8f4fc", text: "#0550ae", border: "#add8f5" },
  Global: { bg: "#e6f9f1", text: "#116329", border: "#a2e2c3" },
  Other: { bg: "#f6f8fa", text: "#6e7781", border: "#d0d7de" },
};

// accent left-border per type
const TYPE_ACCENT = {
  US: "#0550ae",
  Global: "#0b756c",
  Other: "#8c959f",
};

// ─── Org Card ─────────────────────────────────────────────────────────────────

const OrgCard = ({ org }) => {
  const navigate = useNavigate();
  const orgType = getOrgType(org);
  const colors = TYPE_COLORS[orgType] || TYPE_COLORS.Other;
  const accent = TYPE_ACCENT[orgType] || TYPE_ACCENT.Other;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        border: "1px solid #d0d7de",
        borderLeft: `4px solid ${hovered ? accent : "#e0e0e0"}`,
        borderRadius: "10px",
        padding: "20px",
        background: hovered ? "#f9fffe" : "#fff",
        cursor: "pointer",
        transition: "box-shadow 0.18s, border-color 0.18s, background 0.18s, border-left-color 0.18s",
        boxShadow: hovered ? "0 6px 20px rgba(0,0,0,0.09)" : "0 1px 3px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
      onClick={() => navigate(`/ecr-ops/stuck-users/${org._id}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <CorporateFareOutlined style={{ fontSize: 18, color: accent, marginTop: "2px", flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: "14px", color: "#1a1a2e", lineHeight: 1.35 }}>
            {org.name}
          </span>
        </div>
        <span
          style={{
            fontSize: "11px",
            padding: "3px 9px",
            borderRadius: "12px",
            background: colors.bg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {orgType}
        </span>
      </div>

      {/* Divider */}
      <div style={{ height: "1px", background: "#f0f0f0", margin: "0 -4px" }} />

      {/* Meta rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {org.service_owner && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#57606a" }}>
            <PersonOutlined style={{ fontSize: 14, color: "#8c959f" }} />
            <span style={{ fontWeight: 500, color: "#8c959f", minWidth: 78 }}>Service Owner</span>
            <Tooltip title={org.service_owner} arrow placement="top">
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 160,
                  color: "#1a1a2e",
                }}
              >
                {org.service_owner}
              </span>
            </Tooltip>
          </div>
        )}

        {Array.isArray(org.approvers) && org.approvers.length > 0 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", fontSize: "12px", color: "#57606a" }}>
            <SupervisorAccountOutlined style={{ fontSize: 14, color: "#8c959f", marginTop: "1px" }} />
            <span style={{ fontWeight: 500, color: "#8c959f", minWidth: 78, flexShrink: 0 }}>Approvers</span>
            <span style={{ color: "#1a1a2e", lineHeight: 1.4 }}>
              {org.approvers.slice(0, 2).join(", ")}
              {org.approvers.length > 2 && (
                <Tooltip
                  title={org.approvers.slice(2).join(", ")}
                  arrow
                  placement="top"
                >
                  <span
                    style={{
                      marginLeft: 4,
                      color: "#0b756c",
                      fontWeight: 600,
                      cursor: "default",
                    }}
                  >
                    +{org.approvers.length - 2} more
                  </span>
                </Tooltip>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: "auto",
          paddingTop: "4px",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <span
          style={{
            fontSize: "13px",
            color: hovered ? accent : "#8c959f",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            transition: "color 0.15s",
          }}
        >
          View Requests
          <ArrowForwardIos style={{ fontSize: 11 }} />
        </span>
      </div>
    </div>
  );
};

// ─── Skeleton (shimmer animation) ─────────────────────────────────────────────

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position:  600px 0; }
  }
`;

const SkeletonCard = () => (
  <div
    style={{
      border: "1px solid #e0e0e0",
      borderLeft: "4px solid #e8e8e8",
      borderRadius: "10px",
      padding: "20px",
      background: "linear-gradient(90deg, #f6f8fa 25%, #ebebeb 50%, #f6f8fa 75%)",
      backgroundSize: "600px 100%",
      animation: "shimmer 1.4s infinite linear",
      height: "140px",
    }}
  />
);

const OrgListSkeleton = () => (
  <>
    <style>{shimmer}</style>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: "16px",
      }}
    >
      {Array.from({ length: 9 }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
  </>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const OpsOrgList = () => {
  const [search, setSearch] = useState("");
  const [orgTypeFilter, setOrgTypeFilter] = useState("All");

  // Falls back to the default (first) platform until the ECR Ops platform
  // selector (EcrOpsLayout) has resolved a selection.
  const { data: platformData } = useGetPlatformsQuery();
  const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
  const platformId = selectedPlatformId || platformData?._id;

  const { data: orgListData, isLoading, isError } = useGetActiveOrganizationsQuery(
    { platformId },
    { skip: !platformId }
  );

  const orgs = orgListData || [];

  const filteredOrgs = useMemo(() => {
    return orgs.filter((org) => {
      const matchesSearch =
        !search || org.name?.toLowerCase().includes(search.toLowerCase());
      const matchesType =
        orgTypeFilter === "All" || getOrgType(org) === orgTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [orgs, search, orgTypeFilter]);

  // count per type for the filter chips
  const typeCounts = useMemo(() => {
    const counts = { US: 0, Global: 0, Other: 0 };
    orgs.forEach((org) => { counts[getOrgType(org)] = (counts[getOrgType(org)] || 0) + 1; });
    return counts;
  }, [orgs]);

  return (
    <div>
      {/* Page header */}
      <div className="ecr-ops-page-header">
        <p className="ecr-ops-page-header__eyebrow">Operations · Monitoring</p>
        <h1 className="ecr-ops-page-header__title">Stuck Users Monitor</h1>
        <p className="ecr-ops-page-header__sub">
          Select an organisation to review users stuck in Requested or Remove-Requested state for over 24 hours.
        </p>
      </div>

      <div className="ecr-ops-content" style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {/* Filters row */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "1.5rem",
            alignItems: "center",
            flexWrap: "wrap",
            background: "#f6f8fa",
            border: "1px solid #e8eaed",
            borderRadius: "10px",
            padding: "12px 16px",
          }}
        >
          {/* Search box */}
          <div style={{ position: "relative", flex: 1, minWidth: "200px", maxWidth: "380px" }}>
            <GridSearchIcon
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#8c959f",
                fontSize: 18,
              }}
            />
            <input
              type="text"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "7px 30px 7px 34px",
                border: "1px solid #d0d7de",
                borderRadius: "6px",
                fontSize: "13px",
                outline: "none",
                boxSizing: "border-box",
                background: "#fff",
              }}
            />
            {search && (
              <Close
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#8c959f",
                  fontSize: 16,
                }}
              />
            )}
          </div>

          {/* Org type chips */}
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {[
              { value: "All", label: `All  (${orgs.length})` },
              { value: "US", label: `US  (${typeCounts.US})` },
              { value: "Global", label: `Global  (${typeCounts.Global})` },
              { value: "Other", label: `Other  (${typeCounts.Other})` },
            ].map(({ value, label }) => (
              <Chip
                key={value}
                label={label}
                size="small"
                onClick={() => setOrgTypeFilter(value)}
                sx={{
                  fontSize: "12px",
                  fontWeight: orgTypeFilter === value ? 700 : 400,
                  background: orgTypeFilter === value ? "#0b756c" : "#fff",
                  color: orgTypeFilter === value ? "#fff" : "#444",
                  border: "1px solid",
                  borderColor: orgTypeFilter === value ? "#0b756c" : "#d0d7de",
                  cursor: "pointer",
                  "&:hover": { background: orgTypeFilter === value ? "#085f56" : "#f0f0f0" },
                }}
              />
            ))}
          </div>

          <span style={{ marginLeft: "auto", color: "#8c959f", fontSize: "12px", whiteSpace: "nowrap" }}>
            {filteredOrgs.length} org{filteredOrgs.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Content */}
        {isLoading ? (
          <OrgListSkeleton />
        ) : isError ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#cf222e" }}>
            Failed to load organizations. Please try again.
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#6e7781" }}>
            No organizations found.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "14px",
            }}
          >
            {filteredOrgs.map((org) => (
              <OrgCard key={org._id} org={org} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpsOrgList;

